import logger from '../../Utils/logger';
/**
 * BookingTimer context: shared hold-countdown state across Payment,
 * Contact Details, and Review pages.
 *
 * Persists via sessionStorage keys (survives navigation within current tab only):
 *   countdownEndTime, timerActive, timesUp, timerExtend
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getSessionStorageValue,
  removeSessionStorageValue,
  setSessionStorageValue,
  STORAGE_KEYS,
} from '../../Utils/SessionStorage';
import { getCountdownRemainingSeconds } from '../../Utils/bookingHold';
import {
  TIMER_EXTENSION_SECONDS,
  TIMER_EXTENSION_LIMIT,
} from '../../Utils/bookingConfig';
import { useWebSettings } from '../WebSettings/WebSettingsContext';

const BookingTimerContext = createContext(null);

const readInitialTimeLeft = () => getCountdownRemainingSeconds();

const readInitialIsTimerActive = () => getCountdownRemainingSeconds() > 0;

const readInitialIsTimesup = () => {
  if (getSessionStorageValue(STORAGE_KEYS.TIMES_UP)) return true;
  const endTime = sessionStorage.getItem(STORAGE_KEYS.COUNTDOWN_END_TIME);
  const bookingId = getSessionStorageValue(STORAGE_KEYS.BOOKING_ID);
  if (bookingId && endTime && getCountdownRemainingSeconds() <= 0) return true;
  return false;
};

export const BookingTimerProvider = ({ children }) => {
  const { timerValue } = useWebSettings();
  const [isLeaveBookingPopupActive, setIsLeaveBookingPopupActive] = useState(false);
  const [timeLeft, setTimeLeftState] = useState(readInitialTimeLeft);
  const [isTimerActive, setIsTimerActiveState] = useState(readInitialIsTimerActive);
  const [isTimesup, setIsTimesupState] = useState(readInitialIsTimesup);
  // const [isTimerExtend, setIsTimerExtendState] = useState(
  //   () => getSessionStorageValue(STORAGE_KEYS.TIMER_EXTEND) || false
  // );
  const [extensionCount, setExtensionCount] = useState(0);
  const timerRef = useRef(null);

  const setTimeLeft = useCallback((time) => {
    setTimeLeftState(time);
  }, []);

  const setIsTimerActive = useCallback((active) => {
    setIsTimerActiveState(active);
    setSessionStorageValue(STORAGE_KEYS.TIMER_ACTIVE, active);
  }, []);

  const setIsTimesup = useCallback((timesup) => {
    setIsTimesupState(timesup);
    setSessionStorageValue(STORAGE_KEYS.TIMES_UP, timesup);
  }, []);

  // const setIsTimerExtend = useCallback((extend) => {
  //   // setIsTimerExtendState(extend);
  //   setSessionStorageValue(STORAGE_KEYS.TIMER_EXTEND, extend);
  // }, []);

  /**
   * Start the hold countdown. No-op if an active countdown already exists
   * so navigating Payment ↔ Contact Details never restarts the timer.
   * Duration is driven by web-settings `timerValue` (minutes).
   */
  const startTimer = useCallback(() => {
    const remaining = getCountdownRemainingSeconds();
    if (remaining > 0) {
      setTimeLeftState(remaining);
      setIsTimerActiveState(true);
      setSessionStorageValue(STORAGE_KEYS.TIMER_ACTIVE, true);
      setIsTimesupState(false);
      setSessionStorageValue(STORAGE_KEYS.TIMES_UP, false);
      return;
    }

    const timerTotalDurationMs = timerValue * 60 * 1000;
    const endTime = Date.now() + timerTotalDurationMs;
    sessionStorage.setItem(STORAGE_KEYS.COUNTDOWN_END_TIME, endTime.toString());
    setTimeLeftState(Math.floor(timerTotalDurationMs / 1000));
    setIsTimerActiveState(true);
    setSessionStorageValue(STORAGE_KEYS.TIMER_ACTIVE, true);
    setIsTimesupState(false);
    setSessionStorageValue(STORAGE_KEYS.TIMES_UP, false);
    setExtensionCount(0);
  }, [timerValue]);

  const extendTime = useCallback(() => {
    if (extensionCount >= TIMER_EXTENSION_LIMIT) {
      logger.warn(
        `Cannot extend timer; max extensions (${TIMER_EXTENSION_LIMIT}) reached`
      );
      return;
    }
    setTimeLeftState((prev) => {
      const newTimeLeft = prev + TIMER_EXTENSION_SECONDS;
      const newEndTime = Date.now() + newTimeLeft * 1000;
      sessionStorage.setItem(STORAGE_KEYS.COUNTDOWN_END_TIME, newEndTime.toString());
      return newTimeLeft;
    });
    // setIsTimerExtendState(true);
    setSessionStorageValue(STORAGE_KEYS.TIMER_EXTEND, true);
    setIsTimerActiveState(true);
    setSessionStorageValue(STORAGE_KEYS.TIMER_ACTIVE, true);
    setIsTimesupState(false);
    setSessionStorageValue(STORAGE_KEYS.TIMES_UP, false);
    setExtensionCount((prev) => prev + 1);
  }, [extensionCount]);

  const resetTimer = useCallback(() => {
    removeSessionStorageValue(STORAGE_KEYS.COUNTDOWN_END_TIME);
    setTimeLeftState(0);
    setIsTimerActiveState(false);
    setSessionStorageValue(STORAGE_KEYS.TIMER_ACTIVE, false);
    setIsTimesupState(false);
    setSessionStorageValue(STORAGE_KEYS.TIMES_UP, false);
    // setIsTimerExtendState(false);
    setSessionStorageValue(STORAGE_KEYS.TIMER_EXTEND, false);
    setExtensionCount(0);
  }, []);

  /**
   * Clear all timer-related keys after successful payment.
   * Call this in payment success callback to remove stale timer state.
   */
  const clearTimerAfterPayment = useCallback(() => {
    removeSessionStorageValue(STORAGE_KEYS.COUNTDOWN_END_TIME);
    removeSessionStorageValue(STORAGE_KEYS.TIMER_ACTIVE);
    removeSessionStorageValue(STORAGE_KEYS.TIMES_UP);
    removeSessionStorageValue(STORAGE_KEYS.TIMER_EXTEND);
    setTimeLeftState(0);
    setIsTimerActiveState(false);
    setIsTimesupState(false);
    // setIsTimerExtendState(false);
    setExtensionCount(0);
  }, []);

  /** Resume from sessionStorage (mount / refresh). */
  useEffect(() => {
    logger.log('BookingTimerProvider mounted, checking for existing timer state...');
    const remaining = getCountdownRemainingSeconds();
    if (remaining > 0) {
      setTimeLeftState(remaining);
      setIsTimerActiveState(true);
      setSessionStorageValue(STORAGE_KEYS.TIMER_ACTIVE, true);
      return;
    }

    const endTime = sessionStorage.getItem(STORAGE_KEYS.COUNTDOWN_END_TIME);
    const bookingId = getSessionStorageValue(STORAGE_KEYS.BOOKING_ID);
    if (endTime || (bookingId && getSessionStorageValue(STORAGE_KEYS.TIMES_UP))) {
      removeSessionStorageValue(STORAGE_KEYS.COUNTDOWN_END_TIME);
      setIsTimerActiveState(false);
      setSessionStorageValue(STORAGE_KEYS.TIMER_ACTIVE, false);
      logger.log('BookingTimerProvider: Timer expired or no countdown end time found.', bookingId ? `Booking ID: ${bookingId}` : 'No booking ID found.');
      if (bookingId) {
        setIsTimesupState(true);
      logger.log('BookingTimerProvider: Booking ID exists but timer expired. Setting timesUp to true.');
        setSessionStorageValue(STORAGE_KEYS.TIMES_UP, true);
      }
    }
  }, []);

  /** Wall-clock countdown — survives navigation and refresh via countdownEndTime. */
  useEffect(() => {
      logger.log('Timer tick 0');

    if (!isTimerActive) return undefined;

    const tick = () => {
      logger.log('Timer tick');
      const remaining = getCountdownRemainingSeconds();
      setTimeLeftState(remaining);
      logger.log(`Timer tick: ${remaining} seconds remaining`);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setIsTimesupState(true);
      logger.log(`Timer tick: Time's up!`);
        setSessionStorageValue(STORAGE_KEYS.TIMES_UP, true);
        setIsTimerActiveState(false);
        setSessionStorageValue(STORAGE_KEYS.TIMER_ACTIVE, false);
        removeSessionStorageValue(STORAGE_KEYS.COUNTDOWN_END_TIME);
      }
    };

    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => {
      clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [isTimerActive]);

  const value = useMemo(
    () => ({
      timeLeft,
      setTimeLeft,
      isTimerActive,
      setIsTimerActive,
      isTimesup,
      setIsTimesup,
      // isTimerExtend,
      // setIsTimerExtend,
      startTimer,
      extendTime,
      resetTimer,
      clearTimerAfterPayment,
      extensionCount,
      isLeaveBookingPopupActive,
      setIsLeaveBookingPopupActive,
    }),
    [
      timeLeft,
      setTimeLeft,
      isTimerActive,
      setIsTimerActive,
      isTimesup,
      setIsTimesup,
      // isTimerExtend,
      // setIsTimerExtend,
      startTimer,
      extendTime,
      resetTimer,
      clearTimerAfterPayment,
      extensionCount,
      isLeaveBookingPopupActive,
      setIsLeaveBookingPopupActive,
    ]
  );

  return (
    <BookingTimerContext.Provider value={value}>
      {children}
    </BookingTimerContext.Provider>
  );
};

export const useBookingTimer = () => {
  const context = useContext(BookingTimerContext);
  if (!context) {
    throw new Error('useBookingTimer must be used within BookingTimerProvider');
  }
  return context;
};

export default useBookingTimer;
